/**
 * Панель для отображения деталей ошибки AI ассистента
 */

import { useState } from 'react';
import { ChevronDown, ChevronUp, Copy, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { ErrorDetails } from './types';

interface ErrorDetailsPanelProps {
  error: string;
  details: ErrorDetails | null;
  onDismiss?: () => void;
}

export function ErrorDetailsPanel({ error, details, onDismiss: _onDismiss }: ErrorDetailsPanelProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  const handleCopy = async () => {
    const textToCopy = formatErrorForCopy(error, details);

    try {
      await navigator.clipboard.writeText(textToCopy);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div className="mx-3 mb-2 rounded-lg border border-destructive/30 bg-destructive/10 overflow-hidden">
      {/* Основная ошибка */}
      <div className="flex items-start gap-2 px-3 py-2">
        <span className="flex-1 text-xs text-destructive break-words">
          {error}
        </span>

        {details && (
          <Button
            variant="ghost"
            size="icon"
            className="h-5 w-5 shrink-0 text-destructive/70 hover:text-destructive hover:bg-destructive/10"
            onClick={() => setIsExpanded(!isExpanded)}
            title={isExpanded ? 'Скрыть подробности' : 'Показать подробности'}
          >
            {isExpanded ? (
              <ChevronUp className="h-3 w-3" />
            ) : (
              <ChevronDown className="h-3 w-3" />
            )}
          </Button>
        )}
      </div>

      {/* Развёрнутые детали */}
      {isExpanded && details && (
        <div className="border-t border-destructive/20 bg-destructive/5">
          {/* Кнопка копирования */}
          <div className="flex justify-end px-3 py-1.5 border-b border-destructive/10">
            <Button
              variant="ghost"
              size="sm"
              className={cn(
                'h-6 text-xs gap-1.5',
                isCopied
                  ? 'text-green-600 hover:text-green-600'
                  : 'text-muted-foreground hover:text-foreground'
              )}
              onClick={handleCopy}
            >
              {isCopied ? (
                <>
                  <Check className="h-3 w-3" />
                  Скопировано
                </>
              ) : (
                <>
                  <Copy className="h-3 w-3" />
                  Копировать для разработчика
                </>
              )}
            </Button>
          </div>

          {/* Детали ошибки */}
          <div className="px-3 py-2 space-y-2 max-h-64 overflow-y-auto">
            {details.timestamp && (
              <DetailRow label="Время" value={new Date(details.timestamp).toLocaleString('ru-RU')} />
            )}

            {details.statusCode && (
              <DetailRow label="HTTP код" value={String(details.statusCode)} />
            )}

            {details.name && details.name !== 'Error' && (
              <DetailRow label="Тип ошибки" value={details.name} />
            )}

            {details.model && (
              <DetailRow label="Модель" value={details.model} />
            )}

            {details.apiError && (
              <div className="space-y-1">
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  API Error
                </span>
                <pre className="text-[11px] bg-background/50 p-2 rounded border border-destructive/10 overflow-x-auto whitespace-pre-wrap break-words">
                  {JSON.stringify(details.apiError, null, 2)}
                </pre>
              </div>
            )}

            {details.responseBody !== undefined && details.responseBody !== null && !details.apiError && (
              <div className="space-y-1">
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  Response Body
                </span>
                <pre className="text-[11px] bg-background/50 p-2 rounded border border-destructive/10 overflow-x-auto whitespace-pre-wrap break-words">
                  {typeof details.responseBody === 'string'
                    ? details.responseBody
                    : JSON.stringify(details.responseBody as object, null, 2)}
                </pre>
              </div>
            )}

            {details.stack && (
              <div className="space-y-1">
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  Stack Trace
                </span>
                <pre className="text-[10px] bg-background/50 p-2 rounded border border-destructive/10 overflow-x-auto whitespace-pre-wrap break-words font-mono leading-relaxed">
                  {details.stack}
                </pre>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-2 text-xs">
      <span className="text-muted-foreground shrink-0">{label}:</span>
      <span className="text-foreground break-all">{value}</span>
    </div>
  );
}

/**
 * Форматирует ошибку для копирования в буфер обмена
 */
function formatErrorForCopy(error: string, details: ErrorDetails | null): string {
  const lines: string[] = [
    '=== AI Assistant Error Report ===',
    '',
    `Error: ${error}`,
  ];

  if (details) {
    lines.push('');
    lines.push('--- Details ---');

    if (details.timestamp) {
      lines.push(`Timestamp: ${details.timestamp}`);
    }
    if (details.statusCode) {
      lines.push(`HTTP Status: ${details.statusCode}`);
    }
    if (details.name && details.name !== 'Error') {
      lines.push(`Error Type: ${details.name}`);
    }
    if (details.chatId) {
      lines.push(`Chat ID: ${details.chatId}`);
    }
    if (details.adminId) {
      lines.push(`Admin ID: ${details.adminId}`);
    }
    if (details.model) {
      lines.push(`Model: ${details.model}`);
    }
    if (details.routerEnabled !== undefined) {
      lines.push(`Router Enabled: ${details.routerEnabled}`);
    }

    if (details.apiError) {
      lines.push('');
      lines.push('--- API Error ---');
      lines.push(JSON.stringify(details.apiError, null, 2));
    }

    if (details.responseBody && !details.apiError) {
      lines.push('');
      lines.push('--- Response Body ---');
      lines.push(typeof details.responseBody === 'string'
        ? details.responseBody
        : JSON.stringify(details.responseBody, null, 2));
    }

    if (details.stack) {
      lines.push('');
      lines.push('--- Stack Trace ---');
      lines.push(details.stack);
    }
  }

  lines.push('');
  lines.push('================================');

  return lines.join('\n');
}
